import React, { useState, useEffect } from 'react';
import TemplateShell2 from 'app/components/layout/TemplateShell2';
import Balance from 'app/components/payout/Balance';
import RequestPayout from 'app/components/payout/RequestPayout';
import {styled} from '@mui/material/styles';
import { Box, Stack } from '@mui/material';
import PayeeForm from '../../components/payout/PayeeForm';
import { PayoutOnHoldWarning, SaveSuccessAlert, ErrorAlert, WarningAlert } from 'app/components/payout/Alerts';
import { useGetPayoutRequestInfoQuery, useCreatePayoutRequestMutation } from 'app/features/account/api/accountApi';
import PropTypes from 'prop-types';
import { featureFlag } from 'app/util/featureFlag';
import AppBodyWithSidebar from 'app/components/layout/AppBodyWithSidebar';

const CustomStyledBox = styled(Box)(({ theme }) => ({
  [ theme.breakpoints.up('xs') ]: {
    padding: theme.spacing(2.5, 3), // top-bottom, left-right
  },
  [ theme.breakpoints.up('sm') ]: {
    padding: theme.spacing(3.5, 4), // top-bottom, left-right
  },
  pb: {
    xs: theme.spacing(10), // bottom padding for xs
    sm: theme.spacing(10), // bottom padding for sm
  },
}));

const PayoutInfoContainer = ({ onboardingVersion, afterSaveAction, onCancel }) => {
  const { data: payoutRequestData, isLoading: isPayoutDataLoading } = useGetPayoutRequestInfoQuery();
  const [balance, setBalance] = useState({
    payableBalance: 0,
    totalBalance: 0,
  });

  const payoutsEnabled = featureFlag.enabled('PAYOUT_21803');

  const goBackToAccountPage = () => {
    window.location.href = '/account';
  };

  if (!payoutsEnabled) {
    goBackToAccountPage();
  }

  const [payee, setPayee] = useState({
    payeeName: '',
    country: 'US',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    taxableStatus: '0',
    taxPayerId: '',
  });

  const [errors, setErrors] = useState([]);
  const [invalidFields, setInvalidFields] = useState([]);

  const [payout, setPayout] = useState({
    id: null,
    paymentPeriod: '',
    bankName: '',
    accountType: '0', // 0 -> checking, 1 -> savings
    routingNumber: '',
    bankAccountNumber: '',
    accountNumberConfirmation: '',
    email: '',
    amountLeftInAccount: '0.00',
    paxumId: null,
  });
  const [showPayoutOnHoldWarning, setShowPayoutOnHoldWarning] = useState(false);
  /* eslint-disable camelcase */

  const [performCreate, {
    data: createData,
    error: createError,
    isSuccess: isCreateSuccess,
  }] = useCreatePayoutRequestMutation();

  useEffect(() => {
    if (payoutRequestData) {
      const {
        payment_request: {
          id,
          payee_name = '',
          country,
          address_line1 = '',
          address_line2 = '',
          city = '',
          state = '',
          zip_code = '',
          taxable_status = '0',
          tax_payer_id = '',
          payment_type_and_period = '',
          balance_left = '0.00',
        } = {},
        bank_account: {
          routing_number = '',
          account_number = '',
          bank_account_type = 0,
          bank_name = '',
        } = {},
        show_payout_warning,
        user_paxum: {
          email: paxumEmail = '',
          id: paxumId,
        } = {},
      } = payoutRequestData;

      setBalance({
        payableBalance: payoutRequestData.payable_balance,
        totalBalance: payoutRequestData.total_balance,
      });

      setPayee({
        payeeName: payee_name,
        country,
        addressLine1: address_line1,
        addressLine2: address_line2,
        city,
        state,
        zipCode: zip_code,
        taxableStatus: String(taxable_status),
        taxPayerId: tax_payer_id?.replaceAll && tax_payer_id?.replaceAll('-', ''),
      });

      let amountLeftInAccount = parseFloat(balance_left);
      if (Number.isNaN(amountLeftInAccount)) {
        amountLeftInAccount = '0.00';
      } else {
        amountLeftInAccount = amountLeftInAccount.toFixed(2);
      }

      setPayout({
        paymentPeriod: String(payment_type_and_period),
        bankName: bank_name,
        accountType: String(bank_account_type || 0),
        routingNumber: routing_number || '',
        bankAccountNumber: account_number,
        accountNumberConfirmation: account_number,
        id,
        email: paxumEmail,
        paxumId,
        amountLeftInAccount,
      });

      setShowPayoutOnHoldWarning(show_payout_warning);
    }
  }, [payoutRequestData]);
  /* eslint-disable camelcase */

  useEffect(() => {
    if (isCreateSuccess) {
      if (onboardingVersion && afterSaveAction) {
        afterSaveAction();
      }
      setErrors([]);
    }
  }, [isCreateSuccess]);

  useEffect(() => {
    if (createError?.data?.errors) {
      const fields = [];
      const msgs = [];
      createError.data.errors.forEach((err) => {
        if (err.field) {
          fields.push(err.field);
        }
        msgs.push(err.message);
      });
      setInvalidFields(fields);
      setErrors(msgs);
    }
  }, createError);

  const dataForRequest = () => {
    const payerIdFields = {};
    if (payee.taxableStatus === '10') {
      payerIdFields.ssn1 = payee.taxPayerId.slice(0, 3);
      payerIdFields.ssn2 = payee.taxPayerId.slice(3, 5);
      payerIdFields.ssn3 = payee.taxPayerId.slice(5, 9);
    } else if (payee.taxableStatus === '20') {
      payerIdFields.ein1 = payee.taxPayerId.slice(0, 2);
      payerIdFields.ein2 = payee.taxPayerId.slice(2, 10);
    }

    return {
      payment_request: {
        payee_name: payee.payeeName,
        country: payee.country,
        address_line1: payee.addressLine1,
        address_line2: payee.addressLine2,
        city: payee.city,
        state: payee.state,
        zip_code: payee.zipCode,
        taxable_status: payee.taxableStatus,
        balance_left: payout.amountLeftInAccount,
        payment_period: payout.paymentPeriod,
        ...payerIdFields,
      },
      bank_account: {
        bank_name: payout.bankName,
        bank_account_type: payout.accountType,
        routing_number: payout.routingNumber,
        account_number: payout.bankAccountNumber,
        account_number_confirmation: payout.accountNumberConfirmation,
      },
      user_paxum: {
        email: payout.email,
      },
    };
  };

  const save = () => {
    performCreate({
      data: dataForRequest(),
    });
    const scrollTarget = document.getElementById('scroll-target');

    if (scrollTarget.scrollIntoViewIfNeeded) {
      scrollTarget.scrollIntoViewIfNeeded();
    } else {
      scrollTarget.scrollIntoView({
        behavior: 'smooth',
      });
    }
  };

  // User selected that is not in US but the country is still US
  const addressCountryMismatch = payee.country === 'US' && payee.taxableStatus === '0';
  const canSubmit = !isPayoutDataLoading && !addressCountryMismatch;

  if (onboardingVersion) {
    return (
      <CustomStyledBox>
        { createData?.data && <SaveSuccessAlert /> }
        { !createData && showPayoutOnHoldWarning && <PayoutOnHoldWarning /> }
        {
          createData?.data?.notice && <WarningAlert message={createData.data.notice} />
        }
        <ErrorAlert errors={createError?.data?.errors || []} />
        <Balance payableBalance={balance.payableBalance} totalBalance={balance.totalBalance} />
        <PayeeForm
          payee={payee}
          setPayee={setPayee}
          canShowErrors={!isPayoutDataLoading}
          showCountryAddressError={addressCountryMismatch}
          invalidFields={invalidFields}
        />
        <RequestPayout
          payout={payout}
          setPayout={setPayout}
          onSave={save}
          canSubmit={canSubmit}
          onCancel={goBackToAccountPage}
          onboardingVersion={onboardingVersion}
          invalidFields={invalidFields}
        />
        <Stack justifyContent="flex-end" flexDirection="row">
          <a href="#" className="btn secondary btn-cancel" onClick={onCancel}>Cancel</a>
          <button
            type="button"
            className="btn"
            onClick={canSubmit ? save : () => null}
            disabled={!canSubmit}
          >
            Save
          </button>
        </Stack>
      </CustomStyledBox>
    );
  }

  return (
    <AppBodyWithSidebar>
      <TemplateShell2 header="Payout Info">
        <div id="scroll-target" />
        <CustomStyledBox>
          { createData?.data && <SaveSuccessAlert /> }
          { !createData && showPayoutOnHoldWarning && <PayoutOnHoldWarning /> }
          {
            createData?.data?.notice && <WarningAlert message={createData.data.notice} />
          }
          <ErrorAlert errors={errors} />
          <Balance payableBalance={balance.payableBalance} totalBalance={balance.totalBalance} />
          <PayeeForm
            payee={payee}
            setPayee={setPayee}
            canShowErrors={!isPayoutDataLoading}
            showCountryAddressError={addressCountryMismatch}
            invalidFields={invalidFields}
          />
          <RequestPayout
            payout={payout}
            setPayout={setPayout}
            onSave={save}
            canSubmit={canSubmit}
            onCancel={onboardingVersion ? onCancel : goBackToAccountPage}
            invalidFields={invalidFields}
          />
        </CustomStyledBox>
      </TemplateShell2>
    </AppBodyWithSidebar>
  );
};

PayoutInfoContainer.propTypes = {
  onboardingVersion: PropTypes.bool,
  afterSaveAction: PropTypes.func,
  onCancel: PropTypes.func,
};

PayoutInfoContainer.defaultProps = {
  onboardingVersion: false,
  afterSaveAction: null,
  onCancel: () => null,
};

export default PayoutInfoContainer;